import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAppDispatch, useAppSelector } from "../redux";
import { markAllAsRead, markNotificationAsRead, fetchNotifications } from "../redux/reducers/notificationSlice";
import { Badge, Button, View, Text, Icon } from "./base";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Notification } from "../redux/reducers/notificationSlice";
import { useAuth } from "../hooks/useAuth";

interface NotificationBellProps {
  variant?: "default" | "outlined";
  fullWidth?: boolean;
  halfWidth?: boolean;
  isAdminViewingOtherOrg?: boolean;
}

export const NotificationBell = ({ variant = "default", fullWidth = false, halfWidth = false, isAdminViewingOtherOrg = false }: NotificationBellProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const { notifications, totalUnreadCount } = useAppSelector((state) => state.notifications);
  const recentNotifications = notifications.slice(0, 5); // Show 5 most recent

  // Fetch notifications when component mounts and when user changes (auth)
  useEffect(() => {
    if (user) {
      dispatch(fetchNotifications({ limit: 50 }));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (isOpen) {
      // Refresh notifications when popup opens
      dispatch(fetchNotifications({ limit: 50 }));
    }
  }, [isOpen, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Only close if click is outside both the bell and the popup
      if (bellRef.current && !bellRef.current.contains(target) && popupRef.current && !popupRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use click instead of mousedown to allow button clicks to process first
      document.addEventListener("click", handleClickOutside, true);
      return () => document.removeEventListener("click", handleClickOutside, true);
    }
  }, [isOpen]);

  // Calculate popup position when opened
  useEffect(() => {
    if (isOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      // Find the sidebar container to align popup with sidebar's left edge
      const sidebar = bellRef.current.closest(".left-navbar-container");
      const sidebarRect = sidebar?.getBoundingClientRect();
      // Use requestAnimationFrame to ensure popup is rendered before measuring
      requestAnimationFrame(() => {
        if (popupRef.current) {
          const popupHeight = popupRef.current.offsetHeight;
          setPopupPosition({
            top: rect.top - popupHeight - 4,
            // Align with sidebar's content area (left edge + padding)
            left: sidebarRect ? sidebarRect.left + 12 : rect.left,
          });
        }
      });
    }
  }, [isOpen]);

  const handleMarkAllRead = () => {
    dispatch(markAllAsRead());
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate("/notifications");
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <View
      ref={bellRef}
      className={clsx("relative", fullWidth && "w-full", halfWidth && "w-1/2")}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "relative p-2 rounded-md",
          variant === "outlined"
            ? isAdminViewingOtherOrg
              ? "bg-cyan-50 dark:bg-cyan-900/40 border border-cyan-200 dark:border-cyan-700 hover:bg-cyan-100 dark:hover:bg-cyan-800/50"
              : "bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80"
            : "hover:bg-gray-100 dark:hover:bg-gray-800",
          (fullWidth || halfWidth) && "w-full flex justify-center",
        )}
      >
        <Icon
          name="notification"
          size={20}
          className={isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-[#555]"}
        />
        {totalUnreadCount > 0 && (
          <View className="absolute -top-1 -right-1">
            <Badge
              variant="brand"
              className="!text-xs !px-1.5 !min-w-[18px] !h-[18px] flex items-center justify-center"
            >
              {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
            </Badge>
          </View>
        )}
      </Button>

      {isOpen &&
        bellRef.current &&
        ReactDOM.createPortal(
          <View
            ref={popupRef}
            className="fixed w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[10000]"
            style={{
              top: popupPosition.top,
              left: popupPosition.left,
            }}
          >
            <View className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <Text className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</Text>
              {totalUnreadCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Mark all read
                </Button>
              )}
            </View>

            <View className="max-h-96 overflow-y-auto">
              {recentNotifications.length === 0 ? (
                <View className="p-6 text-center">
                  <Text className="text-sm text-gray-500 dark:text-gray-400">No notifications</Text>
                </View>
              ) : (
                recentNotifications.map((notification) => (
                  <NotificationBellItem
                    key={notification.publicId}
                    notification={notification}
                    onClose={() => setIsOpen(false)}
                    formatTimeAgo={formatTimeAgo}
                  />
                ))
              )}
            </View>

            <View className="p-3 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="ghost"
                onClick={handleViewAll}
                className="w-full text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                View all notifications
              </Button>
            </View>
          </View>,
          document.body,
        )}
    </View>
  );
};

interface NotificationBellItemProps {
  notification: Notification;
  onClose: () => void;
  formatTimeAgo: (dateString: string) => string;
}

const NotificationBellItem = ({ notification, onClose, formatTimeAgo }: NotificationBellItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Close the popup first
    onClose();

    // Mark as read if needed (don't await, do it async)
    if (!notification.readAt) {
      dispatch(markNotificationAsRead(notification.publicId)).catch(() => {
        // Error is handled silently
      });
    }

    // Navigate to actionUrl if it exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={(e) => {
        e.stopPropagation(); // Prevent outside click handler from closing popup
      }}
      className={clsx(
        "w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer",
        !notification.readAt && "bg-blue-50 dark:bg-blue-900/20",
      )}
      style={{ pointerEvents: "auto" }}
    >
      <View
        className="flex items-start gap-3"
        style={{ pointerEvents: "none" }}
      >
        <View className="flex-shrink-0 mt-0.5">
          <Icon
            name="info"
            size={16}
            className={clsx(notification.readAt ? "text-gray-400" : "text-blue-500")}
          />
        </View>
        <View className="flex-1 min-w-0">
          <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{notification.title}</Text>
          <Text className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{notification.message}</Text>
          <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatTimeAgo(notification.createdAt)}</Text>
        </View>
        {!notification.readAt && (
          <View className="flex-shrink-0">
            <View className="w-2 h-2 bg-blue-500 rounded-full" />
          </View>
        )}
      </View>
    </button>
  );
};
